import { ReactNode } from 'react';
import { CardMenuItem } from '@tempmee/core';
import { Box, Column, Grid, OverflowMenu, PageHeader } from '@tempmee/react';
import { useMediaQuery } from '~/hooks';
import { COLLAPSE_BREAKPOINT } from './shared/utils/layout';


/**
 * Width of the Figma content container (1400px frame minus its 252px left nav).
 * The shipped `SideBar` is 300px, so on a 1440px viewport the container lands at
 * 1140px and the cap never binds — do not redo this arithmetic from the frame
 * alone.
 */
const CONTAINER_WIDTH = 'min(1148px, 100%)';

/** Fixed sidebar width from the desktop frame; the primary column takes the rest. */
const DESKTOP_COLUMNS = 'minmax(0, 1fr) 400px';

/**
 * Stacked, the column is capped at the width every block declares as its own
 * maximum (`max-w-[450px]` inside `LargeShiftDetails` / `AdditionalDetails` /
 * `Receipt`) and centred. Without the cap the sidebar blocks stop at 450 while
 * the primary keeps filling, which leaves the stack ragged at anything above
 * ~500px. The class resolves because `@tempmee/react-blocks/styles.css` emits it
 * for those blocks — dental-portal has no Tailwind build of its own.
 */
const STACKED_WIDTH = 'max-w-[450px] mx-auto w-full';

export interface IShiftDetailsPageShellProps {
  /** Page title, e.g. "Open Shift Details". Rendered as the page's `<h1>`. */
  title: ReactNode;
  /** Three-dot menu actions. The trigger is hidden when this is empty. */
  actions?: CardMenuItem[];
  /**
   * The shift summary block. Heads the sidebar on desktop and hoists above the
   * primary column when the layout collapses, per the responsive frame.
   */
  summary?: ReactNode;
  /** The page's main content — the 2/3 column on desktop. */
  primary?: ReactNode;
  /** Sidebar content below the summary (Additional Information, Receipt). */
  sidebar?: ReactNode;
}

/**
 * Shared chrome for the four redesigned Shift Details pages: the page header
 * with its overflow menu, the subdued page background and the responsive
 * two-column grid. Purely presentational — every state page owns its own
 * blocks, actions and data.
 *
 * The background matters: the blocks' panel shell is `bg-background` with no
 * border and no shadow, so on a white page they would be invisible.
 */
/**
 * The page's outer frame: the subdued background and the centred content
 * container, with nothing inside them.
 *
 * Split out of the shell so the states that render *before* a page can — the
 * gate's loading spinner, most of all — sit on the same background and inside
 * the same container. Rendered bare, a spinner centres itself against the raw
 * route area on the default white, and then the page arrives on a different
 * ground and a narrower box: the content visibly shifts under the office.
 */
export function ShiftDetailsPageFrame({
  children,
  testID = 'shift-details-shell',
}: {
  children: ReactNode;
  /** Overridden by the loading state so tests can tell the two apart. */
  testID?: string;
}) {
  return (
    <Column flex className="bg-background-subdued w-full" data-cy={testID} data-testid={testID}>
      <Box width={CONTAINER_WIDTH} className="mx-auto w-full">
        {children}
      </Box>
    </Column>
  );
}

export function ShiftDetailsPageShell({
  title,
  actions = [],
  summary,
  primary,
  sidebar,
}: IShiftDetailsPageShellProps) {
  const [isCollapsed] = useMediaQuery([COLLAPSE_BREAKPOINT]);

  return (
    <ShiftDetailsPageFrame>
      <>
        <PageHeader
          variant="sub"
          // The DS header insets its own content by 16px; the body below is on
          // Figma's 24px. Without this the title sits 8px left of every card.
          className="px-6"
          title={title}
          actions={[<OverflowMenu key="overflow" items={actions} label="Shift actions" />]}
        />

        <Box px={6} pb={6}>
          {isCollapsed ? (
            <Column gap={6} className={STACKED_WIDTH} data-testid="shift-details-stacked">
              {summary}
              {primary}
              {sidebar}
            </Column>
          ) : (
            <Grid
              columns={DESKTOP_COLUMNS}
              gap={6}
              align="start"
              data-testid="shift-details-columns"
            >
              <Box>{primary}</Box>
              <Column gap={6}>
                {summary}
                {sidebar}
              </Column>
            </Grid>
          )}
        </Box>
      </>
    </ShiftDetailsPageFrame>
  );
}
